import React from "react";
import { styled } from "goober";

import Tilting from "@/shared/Tilting.jsx";
import { classNames } from "@/util/class-names.mjs";

const focusCss = `
  z-index: 1;

  .card {
    outline-color: var(--shade0-25);

    transition-timing-function: var(--ease-out-expo);
  }
`;

const activeCss = `
  z-index: 1;

  .card-wrapper {
    transform: scale(0.9);
  }
`;

const AnchorCardWrapper = styled<"a" | "button" | "div">("a")`
  width: 100%;

  background-color: transparent;

  outline: none;

  user-select: none;
  user-drag: none;

  .card {
    outline: 2px solid transparent;
    outline-offset: var(--sp-0_5);

    transition: var(--transition-duration) var(--ease-out-quart);
    transition-property: outline, transform;
  }

  .card-wrapper {
    transition: transform var(--transition-duration) var(--ease-out-quart);
  }

  @media (pointer: fine) {
    &:hover {
      ${focusCss}
    }

    &:has(.focusable:hover) {
      ${focusCss}
    }
  }

  &:focus {
    ${focusCss}
  }

  &:has(.focusable:focus) {
    ${focusCss}
  }

  &:has(.focusable:active) {
    ${activeCss}
  }

  &:not(.inert) {
    cursor: pointer;

    &:active {
      ${activeCss}
    }
  }
`;

type CardLink = React.ComponentPropsWithoutRef<"a"> & {
  href: string;
};

type CardDiv = React.ComponentPropsWithoutRef<"div"> & {
  href?: undefined;
};

type CardButton = React.ComponentPropsWithoutRef<"button"> & {
  as: "button";
  href?: undefined;
};

type AnchorCardContainerProps = CardLink | CardDiv | CardButton;

function isCardClickable(props: Partial<AnchorCardContainerProps>) {
  return (
    !!props.href || !!props.onClick || ("as" in props && props.as === "button")
  );
}

/**
 * This adds the outline effect to nested a `.card` element on `:hover` or `:focus` (for keyboard navigation). Your
 * `.card` must be nested within a `.card-wrapper`, which is the component that is scaled and is often a wrapper
 * around the `Tilting` component.
 *
 * If the card is not directly clickable (due to being a div or not having an href), children with `.focusable`
 * can be used to trigger the effect on `:hover` or `:focus`.
 *
 * Unless you want fancier effects such as showing the outline when hovering over an element that's not the card
 * (e.g. a footer outside of the card), you want <AnchorCard> instead.
 */
export function AnchorCardContainer({
  className,
  children,
  ...rest
}: AnchorCardContainerProps) {
  return (
    <AnchorCardWrapper
      as={rest.href ? "a" : "div"}
      {...(rest.href ? { draggable: rest.draggable ?? false } : {})}
      {...classNames(className, !isCardClickable(rest) && "inert")}
      {...rest}
    >
      {children}
    </AnchorCardWrapper>
  );
}

/**
 * Reduces the boilerplate of adding tilt and hover effects to cards.
 */
export default function AnchorCard({ children, ...rest }) {
  return (
    <AnchorCardContainer {...rest}>
      <div className="card-wrapper">
        <Tilting>{children}</Tilting>
      </div>
    </AnchorCardContainer>
  );
}
